/**
 * What the error page (app/error.tsx) does about an error, kept apart from
 * React so it can be tested (tests/unit/error-recovery.test.mjs). Four
 * kinds of error reach it:
 *
 *   - "stale": a lazily loaded piece of an older build that a deploy
 *     replaced. A tab opened before a deploy asks for chunk files the new
 *     build no longer has: on 2026-10-17 a signed-in user on /ru/map got
 *     our "500" seven times in a row ("Loading chunk 8986 failed"). The
 *     page reloads into the new build;
 *   - "browser": thrown in the browser (no digest). "Try again" used to
 *     re-render the same broken state, which threw again within 20 ms: on
 *     2026-11-01 one tab on /ru/analyze showed the "500" 13 times (React
 *     #600 right after the connection dropped and the VPN changed
 *     country) until a hard reload fixed it. The page reports the error,
 *     then reloads once by itself; "Try again" reloads;
 *   - "unavailable": the server render could not reach our API (digest
 *     UNAVAILABLE_DIGEST, lib/seo/api.ts). "Temporarily unavailable", and
 *     the page asks the server again by itself a few times;
 *   - "server": any other server render error (it has a digest). The
 *     "500"; "Try again" asks the server again (router.refresh) and
 *     re-renders, which re-rendering alone never did.
 *
 * At most one automatic reload a minute per tab, and none at all when
 * sessionStorage is blocked or keeps nothing: the page can never reload
 * in a loop. A report that fails never stands in the way.
 */
import { UNAVAILABLE_DIGEST } from "@/lib/unavailable";

export type ErrorKind = "stale" | "browser" | "unavailable" | "server";
export type ErrorLocale = "az" | "ru" | "en";

/** A lazily loaded piece of an older build that the current deployment
 *  no longer serves. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { name?: unknown; message?: unknown };
  if (e.name === "ChunkLoadError") return true;
  const msg = typeof e.message === "string" ? e.message : "";
  return /Loading (CSS )?chunk \S+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg);
}

function digestOf(error: unknown): string | null {
  const d = error && typeof error === "object" ? (error as { digest?: unknown }).digest : undefined;
  return typeof d === "string" && d ? d : null;
}

export function errorKind(error: unknown): ErrorKind {
  if (isChunkLoadError(error)) return "stale";
  const digest = digestOf(error);
  if (digest === UNAVAILABLE_DIGEST) return "unavailable";
  return digest ? "server" : "browser";
}

/** The page's language from its URL (the error page sits outside the
 *  locale provider, like the root 404); Azerbaijani by default. */
export function errorLocale(pathname: string | null | undefined): ErrorLocale {
  const seg = (pathname ?? "").split("/")[1];
  return seg === "ru" || seg === "en" ? seg : "az";
}

// ── Reloading ────────────────────────────────────────────────────────

// The key the stale-build reload has always used: a tab that reloaded
// under the previous build keeps its minute.
const RELOAD_KEY = "menzil-chunk-reload";
const RELOAD_WINDOW_MS = 60_000;
/** How long a browser error's report may hold its reload. */
export const REPORT_WAIT_MS = 1_500;

/** Takes this minute's one automatic reload: true when the page may
 *  reload now (and records it), false when it already did within the
 *  minute or storage is blocked or keeps nothing. */
function claimReload(now: number): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (now - last < RELOAD_WINDOW_MS) return false;
    const stamp = String(now);
    sessionStorage.setItem(RELOAD_KEY, stamp);
    return sessionStorage.getItem(RELOAD_KEY) === stamp;
  } catch {
    return false;
  }
}

const ASSET_KEY = "menzil-asset-reload";

/** May the page reload because a content-hashed file of its build (a
 *  translation file, lib/locale-context.tsx) answered 404? The open page
 *  is from a build a deploy replaced and the static archive does not hold
 *  the file either (2026-11-01 21:57 UTC: /i18n/az/r-3935f037.7b909536.json).
 *  At most once per file per tab: a file that is still missing after the
 *  reload (a deploy that lacks it) never reloads again, so the page can
 *  never loop; and within the minute's one reload the error page uses. */
export function claimAssetReload(file: string, now = Date.now()): boolean {
  try {
    if (sessionStorage.getItem(ASSET_KEY) === file) return false;
    if (!claimReload(now)) return false;
    sessionStorage.setItem(ASSET_KEY, file);
    return sessionStorage.getItem(ASSET_KEY) === file;
  } catch {
    return false;
  }
}

// Decided once per error object: React may render the page twice (Strict
// Mode) and the second look must not spend another reload.
const decisions = new WeakMap<object, "reload" | "show">();

/** Reload the page, or show the error? A stale build or a browser error
 *  reloads once a minute at most; a server error stays on screen. */
export function decide(error: unknown, now = Date.now()): "reload" | "show" {
  const key = error && typeof error === "object" ? error : null;
  const known = key ? decisions.get(key) : undefined;
  if (known) return known;
  const kind = errorKind(error);
  const d = (kind === "stale" || kind === "browser") && claimReload(now) ? "reload" : "show";
  if (key) decisions.set(key, d);
  return d;
}

// ── Reporting ────────────────────────────────────────────────────────

/** The error to Sentry with its kind, digest and path. lib/sentry loads
 *  only now: the error page's own script loads on every page. Never
 *  throws or rejects; a stale build (deploys cause it) and the API's
 *  deliberate 503 are not reported. */
export async function report(error: unknown, path: string | null | undefined): Promise<void> {
  const kind = errorKind(error);
  if (kind === "stale") return;
  const extra = { kind, digest: digestOf(error), path: path ?? null };
  try {
    // The API's deliberate 503 ("unavailable") is back-pressure, not a
    // fault: the page retries by itself and nothing is reported.
    if (kind === "unavailable") return;
    const sentry = await import("@/lib/sentry");
    sentry.captureError(error, extra);
    await sentry.flushErrors(REPORT_WAIT_MS);
  } catch {
    // Reporting never stands between the reader and the page.
  }
}

/** What the page does once the error is on screen: report it, and reload
 *  when decide() said so (a browser error after its report, for at most
 *  REPORT_WAIT_MS). Returns the cleanup that cancels a pending reload. */
export function afterShown(error: unknown, path: string | null | undefined, reload: () => void): () => void {
  let cancelled = false;
  const go = () => {
    if (!cancelled) reload();
  };
  const sent = report(error, path);
  if (decide(error) === "reload") {
    if (errorKind(error) === "stale") go();
    else void Promise.race([sent, new Promise((done) => setTimeout(done, REPORT_WAIT_MS))]).then(go);
  }
  return () => {
    cancelled = true;
  };
}

/** "Try again": a server error asks the server again and re-renders (in
 *  one transition, so the page stays until the answer is there); a
 *  browser error or a stale build reloads the page. */
export function tryAgain(
  kind: ErrorKind,
  act: { reload: () => void; refresh: () => void; reset: () => void; transition: (fn: () => void) => void },
): void {
  if (kind === "server" || kind === "unavailable") {
    act.transition(() => {
      act.refresh();
      act.reset();
    });
  } else {
    act.reload();
  }
}

// ── The unavailable page's own tries ─────────────────────────────────

const RETRY_KEY = "menzil-unavailable-retry";
/** After 5, 15 and 30 seconds; then the button only. A failed try reloads
 *  the page (the router falls back to a full load), so the count lives in
 *  sessionStorage, per path. */
const AUTO_RETRY_MS = [5_000, 15_000, 30_000];
/** Tries count for a path this long; a later outage starts over. */
const AUTO_RETRY_WINDOW_MS = 5 * 60_000;
const retries = new WeakMap<object, number | null>();

/** The delay before the unavailable page asks again by itself, or null
 *  when this path has had its tries (or storage is blocked: without the
 *  count the tries could never end). Spends one try per error object. */
export function autoRetryDelay(error: unknown, path: string, now = Date.now()): number | null {
  const key = error && typeof error === "object" ? error : null;
  if (key && retries.has(key)) return retries.get(key) ?? null;
  let delay: number | null = null;
  try {
    const prev = JSON.parse(sessionStorage.getItem(RETRY_KEY) || "null") as { path?: unknown; n?: unknown; at?: unknown } | null;
    const same = !!prev && prev.path === path && typeof prev.at === "number" && now - prev.at < AUTO_RETRY_WINDOW_MS;
    const n = same && typeof prev?.n === "number" ? prev.n : 0;
    if (n < AUTO_RETRY_MS.length) {
      const next = JSON.stringify({ path, n: n + 1, at: same ? prev?.at : now });
      sessionStorage.setItem(RETRY_KEY, next);
      if (sessionStorage.getItem(RETRY_KEY) === next) delay = AUTO_RETRY_MS[n];
    }
  } catch {
    delay = null;
  }
  if (key) retries.set(key, delay);
  return delay;
}

// ── Copy ─────────────────────────────────────────────────────────────

export interface ErrorCopy {
  /** "500" for a server error only; nothing for the others. */
  code: string | null;
  title: string;
  body: string;
  action: string;
}

// Written per language, siz / вы forms. The error page sits outside the
// locale provider and must not wait for a translation file, so the copy
// lives here (like the root 404's).
const COPY: Record<ErrorLocale, {
  server: Omit<ErrorCopy, "code">;
  browser: Omit<ErrorCopy, "code">;
  stale: Omit<ErrorCopy, "code">;
  unavailable: { title: string; auto: string; later: string; action: string };
}> = {
  az: {
    server: { title: "Xəta baş verdi", body: "Səhifəni aça bilmədik. Bir az sonra yenidən cəhd edin.", action: "Yenidən cəhd edin" },
    browser: { title: "Xəta baş verdi", body: "Səhifə gözlənilmədən dayandı. Davam etmək üçün səhifəni yeniləyin.", action: "Səhifəni yeniləyin" },
    stale: { title: "Sayt yenilənib", body: "Səhifənin bir hissəsi əvvəlki versiyadan qalıb. Səhifəni yeniləyin.", action: "Səhifəni yeniləyin" },
    unavailable: {
      title: "Səhifə müvəqqəti açılmır",
      auto: "Məlumatları indi yükləyə bilmədik. Bir neçə saniyədən sonra özümüz yenidən yoxlayacağıq.",
      later: "Məlumatları indi yükləyə bilmədik. Bir az sonra yenidən cəhd edin.",
      action: "Yenidən cəhd edin",
    },
  },
  ru: {
    server: { title: "Произошла ошибка", body: "Не удалось открыть страницу. Попробуйте ещё раз чуть позже.", action: "Попробовать ещё раз" },
    browser: { title: "Произошла ошибка", body: "Страница неожиданно остановилась. Обновите её, чтобы продолжить.", action: "Обновить страницу" },
    stale: { title: "Сайт обновился", body: "Часть страницы осталась от прежней версии. Обновите страницу.", action: "Обновить страницу" },
    unavailable: {
      title: "Страница временно недоступна",
      auto: "Сейчас не удалось загрузить данные. Через несколько секунд мы попробуем снова сами.",
      later: "Сейчас не удалось загрузить данные. Попробуйте ещё раз чуть позже.",
      action: "Попробовать ещё раз",
    },
  },
  en: {
    server: { title: "Something went wrong", body: "We could not open this page. Please try again in a moment.", action: "Try again" },
    browser: { title: "Something went wrong", body: "The page stopped unexpectedly. Reload it to carry on.", action: "Reload the page" },
    stale: { title: "The site has been updated", body: "Part of this page is from the previous version. Please reload it.", action: "Reload the page" },
    unavailable: {
      title: "This page is temporarily unavailable",
      auto: "We could not load its data just now. We will try again automatically in a few seconds.",
      later: "We could not load its data just now. Please try again in a little while.",
      action: "Try again",
    },
  },
};

/** The page's words; `auto` while the unavailable page still has tries. */
export function errorCopy(kind: ErrorKind, locale: ErrorLocale, auto = false): ErrorCopy {
  const c = COPY[locale];
  if (kind === "unavailable") {
    const u = c.unavailable;
    return { code: null, title: u.title, body: auto ? u.auto : u.later, action: u.action };
  }
  return { code: kind === "server" ? "500" : null, ...c[kind] };
}
